import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    TaskSchema,
    TaskSelectionSummarySchema,
    TaskStatus,
} from "../types/task";
import { CountResponseSchema } from "../schemas/count-response.schema";
import {
    TaskCreateSchema,
    TaskUpdateSchema,
    TaskListQuerySchema,
    TaskBulkUpdateSchema,
    TaskThreadUnreadCountSchema,
    StickyRefreshResponseSchema,
    TaskDocumentPathParamsSchema,
    TaskDocumentLinkResponseSchema,
    TaskDocumentBodySchema,
} from "../schemas/task.schema";
import { ListMessagesResponseSchema } from "../schemas/chat.schema";

export const TaskTimeInStatusItemSchema = z.object({
    status: z.nativeEnum(TaskStatus),
    durationMs: z.number().int().nonnegative(),
});

export const TaskTimeInStatusResponseSchema = z.object({
    timeInStatus: z.array(TaskTimeInStatusItemSchema),
});

export type TaskTimeInStatusItem = z.infer<typeof TaskTimeInStatusItemSchema>;
export type TaskTimeInStatusResponse = z.infer<
    typeof TaskTimeInStatusResponseSchema
>;

export {
    TaskCreateSchema,
    TaskUpdateSchema,
    TaskListQuerySchema,
    TaskBulkUpdateSchema,
} from "../schemas/task.schema";

export type {
    TaskCreate,
    TaskUpdate,
    TaskListQuery,
    TaskBulkUpdate,
} from "../schemas/task.schema";

const c = initContract();

export const taskContract = c.router(
    {
        listTasks: {
            method: "GET",
            path: "/:workspaceId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            query: TaskListQuerySchema,
            responses: {
                200: z.object({ tasks: z.array(TaskSchema) }),
            },
        },
        listTaskThreadUnreadCounts: {
            method: "GET",
            path: "/:workspaceId/thread-unread-counts",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            query: TaskListQuerySchema.extend({
                organizationId: z.string().uuid().optional(),
            }),
            responses: {
                200: z.object({
                    counts: z.array(TaskThreadUnreadCountSchema),
                }),
            },
        },
        listTaskSummaries: {
            method: "GET",
            path: "/:workspaceId/summaries",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            responses: {
                200: z.object({
                    tasks: z.array(TaskSelectionSummarySchema),
                }),
                403: ErrorMessageSchema,
            },
        },
        getTaskDetail: {
            method: "GET",
            path: "/:workspaceId/:taskId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                taskId: z.string().uuid(),
            }),
            responses: {
                200: TaskSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getNumberOfTodoTasks: {
            method: "GET",
            path: "/:workspaceId/todo/count",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            responses: {
                200: CountResponseSchema,
                403: ErrorMessageSchema,
            },
        },
        createTask: {
            method: "POST",
            path: "/:workspaceId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            body: TaskCreateSchema,
            responses: {
                201: TaskSchema,
                400: c.type<{ message: string }>(),
                403: ErrorMessageSchema,
            },
        },
        updateTask: {
            method: "PATCH",
            path: "/:workspaceId/:taskId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                taskId: z.string().uuid(),
            }),
            body: TaskUpdateSchema,
            responses: {
                200: TaskSchema,
                400: c.type<{ message: string }>(),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        bulkUpdateTasks: {
            method: "POST",
            path: "/:workspaceId/bulk-update",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            body: TaskBulkUpdateSchema,
            responses: {
                200: z.object({
                    mode: z.literal("sync"),
                    updatedCount: z.number(),
                }),
                202: z.object({
                    mode: z.literal("async"),
                    jobId: z.string(),
                }),
                400: c.type<{ message: string }>(),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        bulkDeleteTasks: {
            method: "DELETE",
            path: "/:workspaceId/bulk/delete",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            body: z.object({
                taskIds: z.array(z.string().uuid()).min(1).max(500),
            }),
            responses: {
                200: z.object({ deletedCount: z.number() }),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        deleteTask: {
            method: "DELETE",
            path: "/:workspaceId/:taskId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                taskId: z.string().uuid(),
            }),
            responses: {
                200: c.noBody(),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getTimeInStatus: {
            method: "GET",
            path: "/:workspaceId/:taskId/time-in-status",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                taskId: z.string().uuid(),
            }),
            responses: {
                200: TaskTimeInStatusResponseSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        stickyRefresh: {
            method: "POST",
            path: "/:workspaceId/:taskId/sticky-refresh",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                taskId: z.string().uuid(),
            }),
            body: z.object({}),
            responses: {
                200: StickyRefreshResponseSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        linkDocumentsToTask: {
            method: "POST",
            path: "/:workspaceId/:taskId/documents/link",
            pathParams: TaskDocumentPathParamsSchema,
            body: TaskDocumentBodySchema,
            responses: {
                200: TaskDocumentLinkResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        unlinkDocumentsFromTask: {
            method: "POST",
            path: "/:workspaceId/:taskId/documents/unlink",
            pathParams: TaskDocumentPathParamsSchema,
            body: TaskDocumentBodySchema,
            responses: {
                200: TaskDocumentLinkResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getTaskThreadMessages: {
            method: "GET",
            path: "/:workspaceId/:taskId/thread/messages",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                taskId: z.string().uuid(),
            }),
            query: z.object({
                limit: z.coerce
                    .number()
                    .int()
                    .min(1)
                    .max(200)
                    .default(50)
                    .optional(),
                cursor: z.string().optional(),
            }),
            responses: {
                200: ListMessagesResponseSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/tasks",
    },
);
